import { PostgrestError } from "@supabase/supabase-js";

/**
 * Sous RLS, un UPDATE ou un DELETE refusé ne lève aucune erreur : PostgREST répond sans ligne.
 * Un écran qui ne regarde que `error` affiche alors un succès alors que rien n'est enregistré
 * (cas du Directeur sur les tables réservées au propriétaire, novembre 2026).
 *
 * Les écritures concernées de `src/api` relisent donc les lignes touchées (`.select("id")`) et
 * passent par `requireRowsWritten` : aucune ligne devient une erreur, que les écrans traitent
 * déjà comme un échec. Même principe que `src/lib/scheduleWriteOutcome.ts` pour les créneaux.
 */

/** Code de l'erreur fabriquée quand la base n'a touché aucune ligne. */
export const NO_ROW_WRITTEN_CODE = "EQUIPLY_NO_ROW_WRITTEN";

export function noRowWrittenError(message: string): PostgrestError {
  return new PostgrestError({ message, details: "", hint: "", code: NO_ROW_WRITTEN_CODE });
}

/** Vrai quand l'écriture n'a touché aucune ligne (refus RLS, ou ligne supprimée entre-temps). */
export function isNoRowWrittenError(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    (error as { code?: unknown }).code === NO_ROW_WRITTEN_CODE
  );
}

interface RowsResponse<Row> {
  data: Row[] | null;
  error: PostgrestError | null;
}

/**
 * Attend une écriture suivie de `.select(...)` : une réponse sans erreur et sans ligne devient
 * l'erreur `message`.
 */
export async function requireRowsWritten<Row>(
  query: PromiseLike<RowsResponse<Row>>,
  message: string,
): Promise<RowsResponse<Row>> {
  const { data, error } = await query;
  if (error) return { data: null, error };
  if (!data || data.length === 0) return { data: null, error: noRowWrittenError(message) };
  return { data, error: null };
}

/** Variante pour une seule ligne : renvoie la première ligne écrite. */
export async function requireRowWritten<Row>(
  query: PromiseLike<RowsResponse<Row>>,
  message: string,
): Promise<{ data: Row | null; error: PostgrestError | null }> {
  const { data, error } = await requireRowsWritten(query, message);
  return { data: data?.[0] ?? null, error };
}

/** Ids attendus que la base n'a pas touchés. */
export function untouchedRowIds(
  expectedIds: readonly string[],
  rows: ReadonlyArray<{ id: string }> | null | undefined,
): string[] {
  const touched = new Set((rows ?? []).map((row) => row.id));
  return expectedIds.filter((id) => !touched.has(id));
}
